
import {
    ApplicationUserModel,
    ScrapReasonWorkplaceModel,
    ScrapReasonModel,
    WorkplaceModel,
    ScrapReasonWorkplaceStationModel,
    AuditModel,
    QueryOperator,
    AuditWorkplaceModel,
    AuditEscalationGroupModel,
} from '@/libs/Api';
import { CrudAction, CrudReponse } from '@/libs/core/+state/models/crud-action';
import { CrudGetter } from '@/libs/core/+state/models/crud-getter';
import Page from '@/Page.vue';
import { Component, Ref, Watch } from 'vue-property-decorator';
import { applicationUserStore } from '@/libs/user-settings/+state/store';
import { Guid } from '@/libs/common/functions/guid';
import { scrapReasonsStore } from '@/libs/scrap-reasons/+state/store';
import WorkplaceStationList from '@/libs/audits/components/WorkplaceStationList.vue';
import { cloneDeep, isEqual } from 'lodash';
import { scrapReasonWorkplacesStore } from '@/libs/scrap-reasons/+state/store';
import { auditsStore } from '@/libs/audits/+state/store';
import { createCrudQueryPayload } from '@/libs/core/+state/models/crud-query-payload';
import { workplacesStore } from '@/libs/workplaces/+state/store';
import ScrapReasons from '@/libs/settings/pages/ScrapReasons.vue';
import ScrapReasonWorkplaceStationList from '@/libs/settings/components/ScrapReasonWorkplaceStationList.vue';

@Component({
    components: { ScrapReasonWorkplaceStationList, ScrapReasons, WorkplaceStationList },
})
export default class ScrapReasonDialog extends Page {
    id!: string;
    dialog = false;
    valid = true;
    saving = false;
    loading = false;
    applicationUser: ApplicationUserModel = applicationUserStore.useGetter(CrudGetter.Detail) as ApplicationUserModel;
    scrapReason: ScrapReasonModel | null = null;
    scrapReasonOriginal: ScrapReasonModel | null = null;
    deleteDialog = false;
    deleting = false;
    hasChanges = false;

    scrapReasonWorkplaces: {
        workplaceId: string;
        scrapReasonWorkplace: ScrapReasonWorkplaceModel | null | undefined;
        workplace: WorkplaceModel | null | undefined;
    }[] = [];
    selectedWorkplaces: {
        workplaceId: string;
        scrapReasonWorkplace: ScrapReasonWorkplaceModel | null | undefined;
        workplace: WorkplaceModel | null | undefined;
    }[] = [];
    selectedWorkplacesOriginal: {
        workplaceId: string;
        scrapReasonWorkplace: ScrapReasonWorkplaceModel | null | undefined;
        workplace: WorkplaceModel | null | undefined;
    }[] = [];

    tableHeaders = [
        { text: this.$i18n.t('workplaces.name'), value: 'workplace.name', align: 'left', sortable: false, width: '30%' },
        {
            text: this.$i18n.t('workplaces.stations'),
            name: 'scrapReasonWorkplaceStations',
            value: 'scrapReasonWorkplaceStations',
            align: 'left',
            sortable: false,
        },
    ];

    @Ref() form!: any;

    get isNewItem(): boolean {
        return this.scrapReason?.id == Guid.EmptyGuid();
    }

    open(id: string) {
        this.dialog = true;
        this.id = id;
        this.load();
    }

    close() {
        this.dialog = false;
    }

    @Watch('selectedWorkplaces', { deep: true })
    questionGroupsChanged() {
        this.hasChanges = !isEqual(this.scrapReason, this.scrapReasonOriginal);
        if (!this.hasChanges) {
            this.hasChanges = !isEqual(this.selectedWorkplaces, this.selectedWorkplacesOriginal);
        }
    }

    mounted() {
        this.scrapReason = { ...auditsStore.useGetter(CrudGetter.Detail) } as AuditModel;
        this.scrapReasonOriginal = cloneDeep(this.scrapReason);
    }

    public getScrapReasonWorkplaces(): Promise<ScrapReasonWorkplaceModel[]> {
        return new Promise<ScrapReasonWorkplaceModel[]>((resolve, reject) => {
            scrapReasonWorkplacesStore.dispatch(
                CrudAction.GetAll,
                createCrudQueryPayload(undefined, [{ field: 'scrapReasonId', op: QueryOperator.Eq, comparand: this.id }])
            );
            this.subscribe(scrapReasonWorkplacesStore, CrudReponse.GetAll)
                .then((existingScrapReasonWorkplaces: ScrapReasonWorkplaceModel[]) => {
                    resolve(existingScrapReasonWorkplaces);
                })
                .catch((e: any) => {
                    reject(e);
                });
        });
    }

    public getWorkplaces(): Promise<ScrapReasonWorkplaceModel[]> {
        return new Promise<ScrapReasonWorkplaceModel[]>((resolve, reject) => {
            workplacesStore.dispatch(
                CrudAction.GetAll,
                createCrudQueryPayload({
                    field: 'name',
                    index: 1,
                    order: 'asc',
                })
            );
            this.subscribe(workplacesStore, CrudReponse.GetAll)
                .then((existingScrapReasonWorkplaces: WorkplaceModel[]) => {
                    resolve(existingScrapReasonWorkplaces);
                })
                .catch((e: any) => {
                    reject(e);
                });
        });
    }

    private load(): Promise<any> {
        if (this.id != Guid.EmptyGuid()) {
            this.loading = true;
            return new Promise((resolve, reject) => {
                this.loadScrapReason()
                    .then((e) => {
                        this.loadItems()
                            .then(() => {
                                this.loading = false;
                                resolve(e);
                            })
                            .catch((e: any) => {
                                reject(e);
                            });
                    })
                    .catch((e: any) => {
                        reject(e);
                    });
            });
        } else {
            this.scrapReason = {
                id: Guid.EmptyGuid(),
                code: '',
                name: '',
            } as ScrapReasonModel;
            this.scrapReasonOriginal = cloneDeep(this.scrapReason);
            this.scrapReasonWorkplaces = [];
            return this.loadItems();
        }
    }

    private loadScrapReason() {
        return new Promise((resolve, reject) => {
            scrapReasonsStore.dispatch(CrudAction.Get, { id: this.id });
            this.subscribe(scrapReasonsStore, CrudReponse.Get)
                .then((e) => {
                    this.scrapReason = e;
                    this.scrapReasonOriginal = cloneDeep(e);
                    resolve(e);
                })
                .catch((e: any) => {
                    reject(e);
                });
        });
    }

    private save() {
        if (this.form.validate()) {
            const scrapReason = { ...this.scrapReason };
            this.saving = true;
            if (this.id == Guid.EmptyGuid()) {
                scrapReasonsStore.dispatch(CrudAction.Create, { item: scrapReason });
                this.subscribe(scrapReasonsStore, CrudReponse.Create)
                    .then((e: ScrapReasonModel) => {
                        this.scrapReason = e;
                        this.scrapReasonOriginal = cloneDeep(e);
                        this.saveScrapReasonWorkplaces()
                            .then(() => {
                                this.load().then(() => {
                                    this.saving = false;
                                    this.close();
                                });
                            })
                            .catch((e) => {
                                this.saving = false;
                                throw e;
                            });
                    })
                    .catch((e) => {
                        this.saving = false;
                        throw e;
                    });
            } else {
                scrapReasonsStore.dispatch(CrudAction.Update, { item: scrapReason });
                this.subscribe(scrapReasonsStore, CrudReponse.Update)
                    .then((e: ScrapReasonModel) => {
                        this.scrapReason = e;
                        this.scrapReasonOriginal = cloneDeep(e);
                        this.saveScrapReasonWorkplaces()
                            .then(() => {
                                this.load().then(() => {
                                    this.saving = false;
                                    this.close();
                                });
                            })
                            .catch((e) => {
                                this.saving = false;
                                throw e;
                            });
                    })
                    .catch((e) => {
                        this.saving = false;
                        throw e;
                    });
            }
        }
    }

    saveScrapReasonWorkplaces(): Promise<void> {
        return new Promise((resolve, reject) => {
            this.getScrapReasonWorkplaces()
                .then((existingscrapReasonWorkplaces: ScrapReasonWorkplaceModel[]) => {
                    let selectedWorkplaces = this.selectedWorkplaces;
                    if (!this.scrapReason!.useWorkplaceLimitation) {
                        selectedWorkplaces = [];
                    }
                    const scrapReasonWorkplacesToDelete = existingscrapReasonWorkplaces.filter(
                        (x) => selectedWorkplaces.find((n) => n.workplaceId == x.workplaceId) == null
                    );
                    const scrapReasonWorkplacesToInsertOrUpdate = selectedWorkplaces.map((x) => {
                        return {
                            id: x.scrapReasonWorkplace?.id ?? Guid.EmptyGuid(),
                            scrapReasonId: this.scrapReason!.id,
                            workplaceId: x.workplaceId,
                            useStationLimitation: x.scrapReasonWorkplace?.useStationLimitation ?? false,
                            scrapReasonWorkplaceStations: x.scrapReasonWorkplace?.useStationLimitation
                                ? x.scrapReasonWorkplace?.scrapReasonWorkplaceStations ?? []
                                : [],
                        } as ScrapReasonWorkplaceModel;
                    });
                    this.deleteAllPromise(scrapReasonWorkplacesToDelete)
                        .then(() => {
                            this.saveAllPromise(scrapReasonWorkplacesToInsertOrUpdate)
                                .then(() => {
                                    this.loadItems()
                                        .then(() => {
                                            resolve();
                                        })
                                        .catch((e: any) => {
                                            console.error(e);
                                            reject(e);
                                        });
                                })
                                .catch((e: any) => {
                                    console.error(e);
                                    reject(e);
                                });
                        })
                        .catch((e: any) => {
                            console.error(e);
                            reject(e);
                        });
                })
                .catch((e: any) => {
                    console.error(e);
                    reject(e);
                });
        });
    }

    deleteClick() {
        this.deleteDialog = true;
    }

    deleteScrapReason() {
        this.deleting = true;
        scrapReasonsStore.dispatch(CrudAction.Delete, { id: this.id });
        this.subscribe(scrapReasonsStore, CrudReponse.Delete)
            .then((e: any) => {
                this.deleting = false;
                this.deleteDialog = false;
                this.close();
            })
            .catch((e: any) => {
                this.deleting = false;
            });
    }

    private loadItems(): Promise<void> {
        return new Promise((resolve, reject) => {
            this.getScrapReasonWorkplaces()
                .then((existingScrapReasonWorkplaces: ScrapReasonWorkplaceModel[]) => {
                    this.getWorkplaces()
                        .then((workplaces: WorkplaceModel[]) => {
                            const deletedWorkplaces = existingScrapReasonWorkplaces!.filter(
                                (x) => workplaces.find((n) => n.id == x.workplaceId) == null
                            );
                            this.scrapReasonWorkplaces = [
                                ...deletedWorkplaces.map((x) => {
                                    return {
                                        workplaceId: x.workplaceId!,
                                        scrapReasonWorkplace: x,
                                        workplace: x.workplace,
                                    };
                                }),
                            ];
                            this.selectedWorkplaces = [
                                ...deletedWorkplaces.map((x) => {
                                    return {
                                        workplaceId: x.workplaceId!,
                                        scrapReasonWorkplace: null,
                                        workplace: null,
                                    };
                                }),
                            ];
                            this.scrapReasonWorkplaces = [
                                ...this.scrapReasonWorkplaces,
                                ...workplaces.map((x) => {
                                    return {
                                        workplaceId: x.id!,
                                        scrapReasonWorkplace: existingScrapReasonWorkplaces!.find(
                                            (n) => n.workplaceId == x.id
                                        ) ?? {
                                            id: Guid.EmptyGuid(),
                                        },
                                        workplace: x,
                                    };
                                }),
                            ];
                            this.selectedWorkplaces = [
                                ...existingScrapReasonWorkplaces!.map((x) => {
                                    return {
                                        workplaceId: x.workplaceId!,
                                        scrapReasonWorkplace: x,
                                        workplace: x.workplace,
                                    };
                                }),
                            ];
                            this.selectedWorkplacesOriginal = cloneDeep(this.selectedWorkplaces);
                            resolve(undefined);
                        })
                        .catch((e: any) => reject(e));
                })
                .catch((e: any) => reject(e));
        });
    }

    public deleteAllPromise(payload: AuditEscalationGroupModel[]): Promise<any> {
        return new Promise<any>((resolve, reject) => {
            this.deleteAllPromisePop(payload, resolve, reject);
        });
    }

    private deleteAllPromisePop(
        payload: ScrapReasonWorkplaceModel[],
        resolve: (value: any) => void,
        reject: (reason?: any) => void
    ) {
        if (payload.length > 0) {
            const payloadItem = payload[0];
            if (payloadItem.id != Guid.EmptyGuid()) {
                scrapReasonWorkplacesStore.dispatch(CrudAction.Delete, { id: payloadItem.id });
                this.subscribe(scrapReasonWorkplacesStore, CrudReponse.Delete)
                    .then((e: any) => {
                        payload.shift();
                        this.deleteAllPromisePop(payload, resolve, reject);
                    })
                    .catch((e: any) => {
                        reject(e);
                    });
            }
        } else {
            resolve(null);
        }
    }

    public saveAllPromise(payload: AuditWorkplaceModel[]): Promise<any> {
        return new Promise<any>((resolve, reject) => {
            this.saveAllPromisePop(payload, resolve, reject);
        });
    }

    private saveAllPromisePop(
        payload: ScrapReasonWorkplaceModel[],
        resolve: (value: any) => void,
        reject: (reason?: any) => void
    ) {
        if (payload.length > 0) {
            const payloadItem = payload[0];
            if (payloadItem.id == Guid.EmptyGuid()) {
                scrapReasonWorkplacesStore.dispatch(CrudAction.Create, { item: payloadItem });
                this.subscribe(scrapReasonWorkplacesStore, CrudReponse.Create)
                    .then((e: any) => {
                        payload.shift();
                        this.saveAllPromisePop(payload, resolve, reject);
                    })
                    .catch((e: any) => {
                        reject(e);
                    });
            } else {
                scrapReasonWorkplacesStore.dispatch(CrudAction.Update, { item: payloadItem });
                this.subscribe(scrapReasonWorkplacesStore, CrudReponse.Update)
                    .then((e: any) => {
                        payload.shift();
                        this.saveAllPromisePop(payload, resolve, reject);
                    })
                    .catch((e: any) => {
                        reject(e);
                    });
            }
        } else {
            resolve(null);
        }
    }

    isSelected(workplaceId: string): boolean {
        return this.selectedWorkplaces.find((x) => x.workplaceId == workplaceId) != null;
    }

    onItemSelected(e: any, a: any) {
        e.item!.scrapReasonWorkplace!.useStationLimitation = false;
        e.item!.scrapReasonWorkplace!.scrapReasonWorkplaceStations = [];
    }

    getUseStationLimitation(workplaceId: string) {
        return (
            this.selectedWorkplaces.find((x) => x.workplaceId == workplaceId)?.scrapReasonWorkplace?.useStationLimitation ??
            false
        );
    }

    getScrapReasonWorkplaceStations(workplaceId: string) {
        return (
            this.selectedWorkplaces.find((x) => x.workplaceId == workplaceId)?.scrapReasonWorkplace
                ?.scrapReasonWorkplaceStations ?? []
        );
    }

    setUseStationLimitation(workplaceId: string, value: boolean) {
        const selectedWorkplace = this.selectedWorkplaces.find((x) => x.workplaceId == workplaceId);
        if (selectedWorkplace && selectedWorkplace.scrapReasonWorkplace) {
            selectedWorkplace.scrapReasonWorkplace!.useStationLimitation = value;
        }
        this.selectedWorkplaces = cloneDeep(this.selectedWorkplaces);
        this.$forceUpdate();
    }

    setScrapReasonWorkplaceStations(workplaceId: string, scrapReasonWorkplaceStations: ScrapReasonWorkplaceStationModel[]) {
        const selectedWorkplace = this.selectedWorkplaces.find((x) => x.workplaceId == workplaceId);
        if (selectedWorkplace && selectedWorkplace.scrapReasonWorkplace) {
            selectedWorkplace.scrapReasonWorkplace!.scrapReasonWorkplaceStations = scrapReasonWorkplaceStations;
        }
    }
}
